import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/perms";
import { inr, fdate, ymd } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { FormModal } from "@/components/pawkt/FormModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Wallet, Plus, AlertTriangle, TrendingUp, Filter, X, Upload } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = ["Rent", "Salaries", "Utilities", "Marketing", "Software", "Maintenance", "Supplies", "Insurance", "Professional Fees", "Transportation", "Housekeeping", "Staff Welfare", "Taxes/Fees", "Other"].map((c) => ({ value: c.toLowerCase().replace(/[^a-z]/g, "_"), label: c }));

const EXPENSE_TYPES = [
  ["DIRECT_SERVICE_COST", "Direct Service Cost"], ["OPERATING_EXPENSE", "Operating Expense"],
  ["INVENTORY_CONSUMPTION", "Inventory / Consumables"], ["STAFF_WELFARE", "Staff Welfare"],
  ["UTILITIES", "Utilities"], ["MAINTENANCE", "Maintenance"], ["MARKETING", "Marketing"],
  ["ADMINISTRATION", "Administration"], ["FINANCIAL_COST", "Financial Cost"],
  ["SOFTWARE_SUBSCRIPTION", "Software / Subscription"], ["RENT", "Rent"],
  ["PROFESSIONAL_FEE", "Professional Fee"], ["TRANSPORT", "Transport"],
  ["CAPITAL_EXPENDITURE", "Capital Expenditure (CAPEX)"], ["OTHER", "Other"],
].map(([value, label]) => ({ value, label }));

const PAY_METHODS = ["Cash", "UPI", "Card", "Bank Transfer", "Cheque", "Other"].map((m) => ({ value: m, label: m }));

const Checkbox = ({ checked, onChange, label, testid }) => (
  <label className="flex items-center gap-2 cursor-pointer select-none" data-testid={testid}>
    <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 rounded border-slate-300 accent-slate-900" />
    <span className="text-sm text-slate-700">{label}</span>
  </label>
);

const typeLabel = (t) => (EXPENSE_TYPES.find((o) => o.value === t)?.label) || "";

export default function Finance() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const canEdit = hasPerm(user, "expenses.edit");
  const canPrice = hasPerm(user, "dataevents.edit");
  const [expOpen, setExpOpen] = useState(false);
  const [pxOpen, setPxOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [f, setF] = useState({ q: "", category: "all", expense_type: "all", service_id: "all", vendor: "", payment_method: "all", recurring: "all", capex: "all", financial: "all", source: "all", status: "all", from: "", to: "" });

  const { data: summary, isLoading } = useQuery({ queryKey: ["fin-summary"], queryFn: async () => (await api.get("/finance/summary")).data });
  const { data: expenses } = useQuery({ queryKey: ["fin-expenses"], queryFn: async () => (await api.get("/expenses?limit=200")).data });
  const { data: prices } = useQuery({ queryKey: ["fin-prices"], queryFn: async () => (await api.get("/data-events/price-changes")).data });
  const { data: services } = useQuery({ queryKey: ["services-min"], queryFn: async () => (await api.get("/services")).data });
  const { data: deptData } = useQuery({ queryKey: ["expense-departments"], queryFn: async () => (await api.get("/expense-departments")).data });
  const svcOpts = (services || []).map((s) => ({ value: s.id, label: s.name }));
  const svcName = useMemo(() => Object.fromEntries((services || []).map((s) => [s.id, s.name])), [services]);
  const deptOpts = [{ value: "__unallocated__", label: "Unallocated" }, ...((deptData?.departments || []).map((d) => ({ value: d, label: d.charAt(0).toUpperCase() + d.slice(1) })))];

  const createExp = useMutation({ mutationFn: (v) => api.post("/expenses", { ...v, department_id: (!v.department_id || v.department_id === "__unallocated__") ? null : v.department_id }), onSuccess: () => { qc.invalidateQueries({ queryKey: ["fin-expenses"] }); qc.invalidateQueries({ queryKey: ["fin-summary"] }); setExpOpen(false); toast.success("Expense recorded"); }, onError: (e) => toast.error(apiErr(e)) });
  const createPx = useMutation({ mutationFn: (v) => api.post("/data-events/price-changes", v), onSuccess: () => { qc.invalidateQueries({ queryKey: ["fin-prices"] }); setPxOpen(false); toast.success("Price event recorded"); }, onError: (e) => toast.error(apiErr(e)) });

  const s = summary || {};
  const pxRows = prices?.items || [];
  const allExp = expenses?.items || [];

  const filtered = useMemo(() => allExp.filter((e) => {
    const et = (e.expense_type || "");
    if (f.q && !(`${e.category || ""} ${e.subcategory || ""} ${e.description || ""} ${e.vendor || ""}`.toLowerCase().includes(f.q.toLowerCase()))) return false;
    if (f.category !== "all" && (e.category || "other") !== f.category) return false;
    if (f.expense_type !== "all" && et !== f.expense_type) return false;
    if (f.service_id !== "all" && e.service_id !== f.service_id) return false;
    if (f.vendor && !((e.vendor || "").toLowerCase().includes(f.vendor.toLowerCase()))) return false;
    if (f.payment_method !== "all" && e.payment_method !== f.payment_method) return false;
    if (f.recurring !== "all" && (!!e.recurring) !== (f.recurring === "yes")) return false;
    if (f.capex !== "all" && (!!e.is_capex) !== (f.capex === "yes")) return false;
    if (f.financial !== "all" && (et === "FINANCIAL_COST") !== (f.financial === "yes")) return false;
    if (f.source !== "all" && (e.source || "MANUAL") !== f.source) return false;
    if (f.status !== "all" && (e.status || "active") !== f.status) return false;
    if (f.from && (e.expense_date || "") < f.from) return false;
    if (f.to && (e.expense_date || "") > f.to) return false;
    return true;
  }), [allExp, f]);

  const sources = useMemo(() => Array.from(new Set(allExp.map((e) => e.source || "MANUAL"))), [allExp]);
  const activeFilters = Object.entries(f).filter(([k, v]) => v && v !== "all").length;
  const reset = () => setF({ q: "", category: "all", expense_type: "all", service_id: "all", vendor: "", payment_method: "all", recurring: "all", capex: "all", financial: "all", source: "all", status: "all", from: "", to: "" });

  const Sel = ({ k, label, options }) => (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</div>
      <Select value={f[k]} onValueChange={(v) => setF((p) => ({ ...p, [k]: v }))}>
        <SelectTrigger data-testid={`fin-filter-${k}`} className="h-9 bg-white"><SelectValue /></SelectTrigger>
        <SelectContent className="bg-white max-h-64">
          <SelectItem value="all">All</SelectItem>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );

  return (
    <div>
      <PageHeader title="Finance" subtitle="Record business costs, operating result & pricing history">
        {canPrice && <Button variant="outline" onClick={() => setPxOpen(true)} data-testid="fin-new-price-btn" className="gap-1.5"><Plus className="h-4 w-4" /> Price Event</Button>}
        {canEdit && <Button variant="outline" onClick={() => navigate("/expense-import")} data-testid="fin-bulk-import-btn" className="gap-1.5"><Upload className="h-4 w-4" /> Bulk Import</Button>}
        {canEdit && <Button onClick={() => setExpOpen(true)} data-testid="fin-new-expense-btn" className="bg-slate-900 text-white gap-1.5"><Plus className="h-4 w-4" /> Record Business Expense</Button>}
      </PageHeader>

      {isLoading ? <Loading /> : (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="fin-summary">
          <h3 className="font-display text-lg font-semibold text-slate-900 flex items-center gap-2 mb-4"><TrendingUp className="h-5 w-5 text-slate-400" /> Business financial summary</h3>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
            {[["Net revenue", s.net_revenue, "fin-net-revenue"], ["COGS", s.cogs, "fin-cogs"], ["Commissions", s.commissions, "fin-commissions"], ["Staff costs", s.staff_costs, "fin-staff"], ["Operating expenses", s.operating_expenses_total, "fin-opex"], ["Contribution margin", s.contribution_margin, "fin-cm"], ["Operating result", s.operating_result, "fin-opresult"]].map(([l, v, tid]) => (
              <div key={l} className="rounded-lg border border-slate-200 px-3.5 py-3" data-testid={tid}>
                <div className="text-xs text-slate-500">{l}</div>
                <div className="mt-1 font-display text-xl font-bold text-slate-900">{inr(v || 0)}</div>
              </div>
            ))}
          </div>
          {!s.profit_complete && (
            <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="fin-profit-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{s.profit_note || "Profit incomplete — some operating expenses have not been recorded."}</span>
            </div>
          )}
          <p className="mt-2 text-xs text-slate-400">Contribution margin is not profit. Revenue reflects captured payments net of refunds from the canonical financial system.</p>
        </div>
      )}

      <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
        <Tabs defaultValue="expenses">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="expenses" data-testid="fin-tab-expenses">Expenses ({allExp.length})</TabsTrigger>
            <TabsTrigger value="prices" data-testid="fin-tab-prices">Price Events ({pxRows.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="expenses" className="mt-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="relative flex-1 max-w-xs">
                <Input data-testid="fin-expense-search" value={f.q} onChange={(e) => setF((p) => ({ ...p, q: e.target.value }))} placeholder="Search category, vendor, note…" className="h-9 bg-white" />
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowFilters((v) => !v)} data-testid="fin-toggle-filters" className="gap-1.5"><Filter className="h-4 w-4" /> Filters{activeFilters ? ` (${activeFilters})` : ""}</Button>
              {activeFilters > 0 && <Button variant="ghost" size="sm" onClick={reset} data-testid="fin-clear-filters" className="gap-1 text-slate-500"><X className="h-3.5 w-3.5" /> Clear</Button>}
            </div>
            {showFilters && (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="fin-filter-panel">
                <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">From</div><Input type="date" data-testid="fin-filter-from" value={f.from} onChange={(e) => setF((p) => ({ ...p, from: e.target.value }))} className="h-9 bg-white" /></div>
                <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">To</div><Input type="date" data-testid="fin-filter-to" value={f.to} onChange={(e) => setF((p) => ({ ...p, to: e.target.value }))} className="h-9 bg-white" /></div>
                <Sel k="category" label="Category" options={CATEGORIES} />
                <Sel k="expense_type" label="Expense Type" options={EXPENSE_TYPES} />
                <Sel k="service_id" label="Service" options={svcOpts} />
                <Sel k="payment_method" label="Payment Method" options={PAY_METHODS} />
                <Sel k="recurring" label="Recurring" options={[{ value: "yes", label: "Recurring" }, { value: "no", label: "One-off" }]} />
                <Sel k="capex" label="CAPEX" options={[{ value: "yes", label: "CAPEX" }, { value: "no", label: "Operating" }]} />
                <Sel k="financial" label="Financial Cost" options={[{ value: "yes", label: "Financial" }, { value: "no", label: "Non-financial" }]} />
                <Sel k="source" label="Source" options={sources.map((s2) => ({ value: s2, label: s2 }))} />
                <Sel k="status" label="Status" options={[{ value: "active", label: "Active" }, { value: "void", label: "Void" }]} />
                <div><div className="text-[10px] font-bold uppercase tracking-wide text-slate-400 mb-1">Vendor</div><Input data-testid="fin-filter-vendor" value={f.vendor} onChange={(e) => setF((p) => ({ ...p, vendor: e.target.value }))} placeholder="Vendor" className="h-9 bg-white" /></div>
              </div>
            )}
            {filtered.length === 0 ? <Empty icon={Wallet} title="No expenses" subtitle={activeFilters ? "No expenses match these filters." : "Record business costs to complete your financials."} /> : (
              <div className="divide-y divide-slate-100" data-testid="fin-expenses-list">
                {filtered.map((e) => (
                  <div key={e.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`fin-expense-${e.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-800 capitalize flex items-center gap-2 flex-wrap">
                        <span>{(e.category || "other").replace(/_/g, " ")}{e.subcategory ? ` · ${e.subcategory}` : ""}</span>
                        {e.is_capex && <span className="text-[10px] font-bold uppercase rounded bg-indigo-100 text-indigo-700 px-1.5 py-0.5" data-testid={`fin-capex-badge-${e.id}`}>CAPEX</span>}
                        {e.expense_type === "FINANCIAL_COST" && <span className="text-[10px] font-bold uppercase rounded bg-amber-100 text-amber-700 px-1.5 py-0.5">Financial</span>}
                        {e.recurring && <span className="text-[10px] font-bold uppercase rounded bg-slate-100 text-slate-600 px-1.5 py-0.5">Recurring</span>}
                      </div>
                      <div className="text-xs text-slate-400 truncate">{e.description || e.notes || typeLabel(e.expense_type) || "—"} · {e.vendor || "no vendor"} · {fdate(e.expense_date)}{e.service_id ? ` · ${svcName[e.service_id] || "service"}` : ""} · <span className="capitalize">{e.department_id || "Unallocated"}</span> · {e.source || "MANUAL"}</div>
                    </div>
                    <div className="font-semibold text-slate-900 shrink-0">{inr((e.amount || 0) + (e.tax || 0))}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="prices" className="mt-4">
            {pxRows.length === 0 ? <Empty icon={TrendingUp} title="No price events" subtitle="Record price changes — historical invoices remain unchanged." /> : (
              <div className="divide-y divide-slate-100" data-testid="fin-prices-list">
                {pxRows.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 py-2.5 text-sm" data-testid={`fin-price-${p.id}`}>
                    <div className="flex-1"><div className="font-medium text-slate-800">{p.change_type || "service"} price change</div><div className="text-xs text-slate-400">{p.reason || "—"} · {fdate(p.start_date || p.created_at)}</div></div>
                    <div className="text-slate-500">{inr(p.old_price || 0)} → <span className="font-semibold text-slate-900">{inr(p.new_price || 0)}</span></div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <FormModal open={expOpen} onOpenChange={setExpOpen} title="Record Business Expense" testid="fin-expense-modal"
        description="Record a cost in seconds — only date, category and amount are required."
        initial={{ category: "rent", department_id: "__unallocated__", expense_type: "OPERATING_EXPENSE", payment_method: "Cash", recurring: false, is_capex: false, expense_date: ymd() }}
        fields={[
          { name: "expense_date", label: "Date", type: "date" },
          { name: "category", label: "Category", type: "select", options: CATEGORIES },
          { name: "amount", label: "Amount (₹)", type: "number" },
          { name: "expense_type", label: "Expense Type", type: "select", options: EXPENSE_TYPES },
          { name: "department_id", label: "Service / Department", type: "select", options: deptOpts },
          { name: "service_id", label: "Service (optional)", type: "select", options: svcOpts },
          { name: "subcategory", label: "Subcategory" },
          { name: "vendor", label: "Vendor" },
          { name: "quantity", label: "Quantity", type: "number" },
          { name: "unit", label: "Unit (e.g. kg, box)" },
          { name: "unit_cost", label: "Unit Cost (₹)", type: "number" },
          { name: "tax", label: "Tax (₹)", type: "number" },
          { name: "payment_method", label: "Payment Method", type: "select", options: PAY_METHODS },
          { name: "reference_no", label: "Reference No." },
          { name: "description", label: "Description", full: true },
          { name: "flags", label: "Flags", type: "custom", full: true, render: (v, set) => (
            <div className="flex items-center gap-6 pt-1">
              <Checkbox testid="fin-exp-recurring" checked={v.recurring} onChange={(c) => set("recurring", c)} label="Recurring expense" />
              <Checkbox testid="fin-exp-capex" checked={v.is_capex} onChange={(c) => set("is_capex", c)} label="Capital expenditure (CAPEX)" />
            </div>
          ) },
          { name: "notes", label: "Notes", type: "textarea", full: true },
        ]} onSubmit={(v) => createExp.mutateAsync(v)} submitLabel="Record Expense" />

      <FormModal open={pxOpen} onOpenChange={setPxOpen} title="Record Price Event" testid="fin-price-modal"
        initial={{ change_type: "service" }}
        fields={[
          { name: "change_type", label: "Type", type: "select", options: [{ value: "service", label: "Service" }, { value: "product", label: "Product" }, { value: "membership", label: "Membership" }] },
          { name: "service_id", label: "Service (optional)", type: "select", options: svcOpts, full: true },
          { name: "old_price", label: "Old price (₹)", type: "number" },
          { name: "new_price", label: "New price (₹)", type: "number" },
          { name: "start_date", label: "Effective from", type: "date" },
          { name: "end_date", label: "Until", type: "date" },
          { name: "reason", label: "Reason", full: true },
        ]} onSubmit={(v) => createPx.mutateAsync(v)} submitLabel="Record" />
    </div>
  );
}
